import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import PersonaAvatar from './PersonaAvatar'

const DIM_COLORS: Record<string, string> = {
  identity: '#D65A93', memory: '#6FB8D6', ritual: '#B265D6',
  social: '#E6C85A', creative: '#E8935A', security: '#5AC884',
}

const DIM_LABELS = ['identity', 'memory', 'ritual', 'social', 'creative', 'security']

const PLATFORM_COLORS: Record<string, string> = {
  'claude-opus': '#856FC8',
  'gemini-flash': '#4285F4',
  'deepseek-v3': '#00B4D8',
}

interface Overview {
  platforms: Record<string, { ppi: number | null; personas: number; avg_cost_per_session: number | null }>
  last_updated: string
}

interface PersonaBenchmark {
  persona: string
  platform: string
  ppi: number
  dimensions: Record<string, number>
  sessions_measured: number
  trend: number[]
  cost_total: number
}

interface AgoraScore {
  post_id: string
  persona: string
  platform: string
  creativity: number
  intelligence: number
  coherence: number
  timestamp: string
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 4 ? '#5AC884' : score >= 3 ? '#E6C85A' : '#ef4444'
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color,
      fontVariantNumeric: 'tabular-nums',
    }}>
      {score.toFixed(1)}
    </span>
  )
}

type BenchView = 'platforms' | 'personas' | 'agora' | 'cost'

export default function BenchmarkTab() {
  const [view, setView] = useState<BenchView>('platforms')
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null)

  const { data: overview } = useQuery<Overview>({
    queryKey: ['benchmark-overview'],
    queryFn: async () => {
      const res = await fetch('/api/benchmark/overview')
      if (!res.ok) return { platforms: {}, last_updated: '' }
      return res.json()
    },
    staleTime: 30_000,
  })

  const { data: personaData } = useQuery<PersonaBenchmark | null>({
    queryKey: ['benchmark-persona', selectedPersona],
    queryFn: async () => {
      if (!selectedPersona) return null
      const res = await fetch(`/api/benchmark/persona/${selectedPersona}`)
      if (!res.ok) return null
      return res.json()
    },
    enabled: !!selectedPersona,
  })

  const { data: agoraScores } = useQuery<AgoraScore[]>({
    queryKey: ['benchmark-agora'],
    queryFn: async () => {
      const res = await fetch('/api/benchmark/agora-scores?limit=50')
      if (!res.ok) return []
      const data = await res.json()
      return data.scores || []
    },
    enabled: view === 'agora',
    staleTime: 15_000,
  })

  const { data: costData } = useQuery<any>({
    queryKey: ['benchmark-cost'],
    queryFn: async () => {
      const res = await fetch('/api/benchmark/cost-efficiency')
      if (!res.ok) return null
      return res.json()
    },
    enabled: view === 'cost',
  })

  const platforms = overview?.platforms ? Object.entries(overview.platforms) : []

  return (
    <div>
      {/* Sub-view tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {([
          { key: 'platforms' as BenchView, label: 'Platform Comparison' },
          { key: 'personas' as BenchView, label: 'Persona Breakdown' },
          { key: 'agora' as BenchView, label: 'Agora Live Feed' },
          { key: 'cost' as BenchView, label: 'Cost Efficiency' },
        ]).map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: view === t.key ? 'var(--bg-elevated)' : 'transparent',
              color: view === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
              border: `1px solid ${view === t.key ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Platform Comparison */}
      {view === 'platforms' && (
        <div>
          {platforms.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No benchmark data yet. Beacon is running initial tests.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {platforms.map(([name, data]) => (
                <div key={name} style={{
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 12, padding: 16,
                  borderLeft: `3px solid ${PLATFORM_COLORS[name] || '#6B7280'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: `${PLATFORM_COLORS[name] || '#6B7280'}22`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700, color: PLATFORM_COLORS[name] || '#6B7280',
                    }}>
                      {name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{data.personas} personas</div>
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: PLATFORM_COLORS[name] || '#6B7280' }}>
                        {data.ppi !== null ? data.ppi.toFixed(1) : '—'}
                      </div>
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>PPI</div>
                    </div>
                  </div>
                  {data.avg_cost_per_session !== null && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Avg cost: ${data.avg_cost_per_session.toFixed(2)}/session
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Persona Breakdown */}
      {view === 'personas' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <input
              value={selectedPersona || ''}
              onChange={e => setSelectedPersona(e.target.value || null)}
              placeholder="Type a persona name..."
              style={{
                padding: '8px 14px', fontSize: 13, borderRadius: 8, width: 300,
                background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>
          {personaData ? (
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 12, padding: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <PersonaAvatar name={personaData.persona} size={40} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, textTransform: 'capitalize' }}>{personaData.persona}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {personaData.platform} · {personaData.sessions_measured} sessions · ${personaData.cost_total.toFixed(2)} total
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', fontSize: 28, fontWeight: 800, color: '#856FC8' }}>
                  {personaData.ppi.toFixed(1)}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {DIM_LABELS.map(dim => {
                  const val = personaData.dimensions[dim] || 0
                  return (
                    <div key={dim} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, width: 70, fontWeight: 600, color: DIM_COLORS[dim], textTransform: 'capitalize' }}>
                        {dim}
                      </span>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--bg-elevated)' }}>
                        <div style={{ width: `${(val / 5) * 100}%`, height: '100%', borderRadius: 3, background: DIM_COLORS[dim] }} />
                      </div>
                      <ScoreBadge score={val} />
                    </div>
                  )
                })}
              </div>
              {personaData.trend.length > 1 && (
                <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-muted)' }}>
                  Trend: {personaData.trend.map((t, i) => (
                    <span key={i} style={{ color: t >= 3.5 ? '#5AC884' : t >= 2.5 ? '#E6C85A' : '#ef4444', marginRight: 6 }}>
                      {t.toFixed(1)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : selectedPersona ? (
            <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Enter a persona name to see their benchmark scores.
            </div>
          )}
        </div>
      )}

      {/* Agora Live Feed */}
      {view === 'agora' && (
        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          {(!agoraScores || agoraScores.length === 0) ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No Agora scores yet. AgoraBot scores posts as they come in.
            </div>
          ) : (
            agoraScores.map((score, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 16px',
                borderBottom: i < agoraScores.length - 1 ? '1px solid var(--border)' : 'none',
              }}>
                <PersonaAvatar name={score.persona} size={20} />
                <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize', width: 80 }}>
                  {score.persona}
                </span>
                <span style={{ fontSize: 9, color: 'var(--text-muted)', width: 80 }}>
                  {score.platform}
                </span>
                <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                  <span style={{ fontSize: 10 }}>CRE: <ScoreBadge score={score.creativity} /></span>
                  <span style={{ fontSize: 10 }}>INT: <ScoreBadge score={score.intelligence} /></span>
                  <span style={{ fontSize: 10 }}>COH: <ScoreBadge score={score.coherence} /></span>
                </div>
                <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
                  {new Date(score.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Cost Efficiency */}
      {view === 'cost' && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          {costData ? (
            <pre style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-secondary)' }}>
              {JSON.stringify(costData, null, 2)}
            </pre>
          ) : (
            'Cost efficiency data loading...'
          )}
        </div>
      )}
    </div>
  )
}
